import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_COLUMN_WIDTH_V815 = 72;
const MAX_COLUMN_WIDTH_V815 = 720;

const clampColumnWidthV815 = (value) =>
  Math.min(
    MAX_COLUMN_WIDTH_V815,
    Math.max(MIN_COLUMN_WIDTH_V815, Math.round(Number(value) || 0))
  );

const loadColumnWidthsV815 = (defaults, storageKey) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(defaults).map(([key, fallback]) => [
          key,
          clampColumnWidthV815(parsed[key] ?? fallback),
        ])
      );
    }
  } catch {}
  return { ...defaults };
};

const useResizableColumnsV815 = (defaults, storageKey) => {
  const [widths, setWidths] = useState(() =>
    loadColumnWidthsV815(defaults, storageKey)
  );
  const cleanupRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {}
  }, [storageKey, widths]);

  useEffect(
    () => () => {
      cleanupRef.current?.();
    },
    []
  );

  const startResize = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      cleanupRef.current?.();

      const startX = event.clientX;
      const startWidth = widths[columnKey] ?? defaults[columnKey];
      document.body.classList.add('column-resizing-v815');

      const handleMove = (moveEvent) => {
        const nextWidth = clampColumnWidthV815(
          startWidth + moveEvent.clientX - startX
        );
        setWidths((current) =>
          current[columnKey] === nextWidth
            ? current
            : { ...current, [columnKey]: nextWidth }
        );
      };
      const cleanup = () => {
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', cleanup);
        document.removeEventListener('pointercancel', cleanup);
        document.body.classList.remove('column-resizing-v815');
        cleanupRef.current = null;
      };

      cleanupRef.current = cleanup;
      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', cleanup);
      document.addEventListener('pointercancel', cleanup);
    },
    [defaults, widths]
  );

  const resetColumn = useCallback(
    (columnKey) => {
      setWidths((current) => ({
        ...current,
        [columnKey]: defaults[columnKey],
      }));
    },
    [defaults]
  );

  const resetAll = useCallback(() => setWidths({ ...defaults }), [defaults]);

  return { widths, startResize, resetColumn, resetAll };
};

export default useResizableColumnsV815;
